import { useEffect, useState } from 'react';
import { Download, X, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'update_prompt_dismissed_for';

/**
 * Shows a dismissible "Update available" banner when the installed
 * Android app is older than the latest release advertised by the server.
 *
 * Visible only when running inside the native Android wrapper
 * (detected via `window.AndroidBridge.isNativeApp`).
 */
export default function UpdatePrompt() {
  const onDownloadPage = /^\/(download|install)(\/|$)/.test(window.location.pathname);
  const [release, setRelease] = useState(null);
  const [installedCode, setInstalledCode] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const [webUpdate, setWebUpdate] = useState(false);

  useEffect(() => {
    // The app WebView can stay open for days, so re-check whenever it comes back to the foreground and every 6 h
    // (not only at first load) — this is how social-only members, who rarely reload, get the APK / web update prompts.
    const bridge = window.AndroidBridge;
    const native = bridge && typeof bridge.getVersionCode === 'function';
    let code = 0;
    if (native) { try { code = Number(bridge.getVersionCode()) || 0; } catch (e) { /* noop */ } }
    if (code) setInstalledCode(code);
    let cancelled = false;
    const currentBundle = (() => { const sc = Array.from(document.scripts).map(x => x.src).find(x => /\/static\/js\/main\.[a-z0-9]+\.js/.test(x)); return sc ? sc.match(/main\.([a-z0-9]+)\.js/)[1] : null; })();
    const check = () => {
      if (code) {
        fetch(`${API}/api/app/latest-version`, { cache: 'no-store' }).then(r => r.ok ? r.json() : null).then(data => { if (!cancelled && data) setRelease(data); }).catch(() => {});
      }
      if (currentBundle) {
        fetch(`/?_probe=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'text/html' } }).then(r => r.ok ? r.text() : '').then(html => {
          const m = html.match(/main\.([a-z0-9]+)\.js/);
          if (!cancelled && m && m[1] !== currentBundle) setWebUpdate(true);
        }).catch(() => {});
      }
    };
    check();
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    const iv = setInterval(check, 6 * 3600 * 1000);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVis); clearInterval(iv); };
  }, []);

  useEffect(() => {
    if (!release) return;
    const lastDismissed = localStorage.getItem(DISMISS_KEY);
    if (lastDismissed && Number(lastDismissed) >= Number(release.version_code)) {
      setDismissed(true);
    }
  }, [release]);

  if (onDownloadPage) return null;

  if (webUpdate && (!release || !installedCode || Number(release.version_code) <= (installedCode || 0))) {
    return (
      <div className="fixed bottom-3 left-3 right-3 sm:left-auto sm:w-96 z-[70] rounded-xl border bg-white shadow-xl p-3 flex items-center gap-3" data-testid="web-update-prompt">
        <div className="flex-1 text-sm"><p className="font-semibold">A new version of BBC is ready</p><p className="text-xs text-muted-foreground">Reload to get the latest fixes and features.</p></div>
        <button type="button" className="rounded-lg bg-forest-600 text-white text-sm px-3 py-1.5" onClick={() => { const u = new URL(window.location.href); u.searchParams.set('_v', String(Date.now())); window.location.replace(u.toString()); }} data-testid="web-update-reload">Reload</button>
        <button type="button" className="text-xs text-muted-foreground" onClick={() => setWebUpdate(false)} data-testid="web-update-later">Later</button>
      </div>
    );
  }

  if (!release || !installedCode) return null;
  if (Number(release.version_code) <= installedCode) return null;

  const mandatory = !!release.mandatory;
  if (!mandatory && dismissed) return null;

  const handleInstall = () => {
    if (!release.apk_url) return;
    // Let Android handle the APK URL: it triggers the installer prompt.
    window.location.href = release.apk_url;
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(release.version_code));
    setDismissed(true);
  };

  return (
    <div
      className="fixed top-3 left-3 right-3 z-[60] sm:left-auto sm:right-4 sm:top-4 sm:max-w-sm"
      data-testid="update-prompt"
    >
      <div className="rounded-xl border border-forest-200 bg-gradient-to-br from-forest-50 to-white shadow-xl p-3 flex gap-3 items-start">
        <div className="shrink-0 w-9 h-9 rounded-full bg-forest-600 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-forest-900 leading-tight">
                Update available — v{release.version_name}
              </p>
              {release.release_notes ? (
                <p className="text-xs text-gray-600 mt-1 leading-snug break-words">
                  {release.release_notes}
                </p>
              ) : null}
            </div>
            {!mandatory && (
              <button
                onClick={handleDismiss}
                className="shrink-0 p-1 rounded-full hover:bg-gray-100 -mr-1 -mt-1"
                aria-label="Dismiss"
                data-testid="update-prompt-dismiss"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
          <button
            onClick={handleInstall}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-medium px-3 py-1.5 rounded-lg"
            data-testid="update-prompt-install"
          >
            <Download className="w-4 h-4" />
            Install v{release.version_name}
          </button>
        </div>
      </div>
    </div>
  );
}
